
/**
 * Module dependencies.
 */

import { color, units } from 'src/styles/utils';
import React, { ReactElement } from 'react';
import ReactPlayer, { ReactPlayerProps } from 'react-player/lazy';
import Svg from 'src/components/core/svg';
import playIcon from 'src/assets/svg/play.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = Pick<ReactPlayerProps, 'controls' | 'muted' | 'playsinline' | 'volume'> & {
  className?: string,
  image?: string | null,
  loop?: boolean,
  playing?: boolean,
  stopOnUnmount?: boolean,
  url: string
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: ${color('grey100')};
  padding-top: 56.25%;
  position: relative;
  width: 100%;
`;

/**
 * `StyledReactPlayer` styled component.
 */

const StyledReactPlayer = styled(ReactPlayer)`
  left: 0;
  position: absolute;
  top: 0;
`;

/**
 * Export `PlayVideoButton` styled component.
 */

export const PlayVideoButton = styled(Svg).attrs(({ size }) => ({
  icon: playIcon,
  size: size ?? units(8),
  type: 'button'
}))`
  appearance: none;
  background: transparent;
  border: 0;
  box-shadow: none;
  color: ${color('white')};
  left: 50%;
  padding: 0;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
`;

/**
 * `VideoPlayer` component.
 */

const VideoPlayer = (props: Props): ReactElement => {
  const { className, controls, image, url, volume, ...rest } = props;

  return (
    <Wrapper className={className}>
      <StyledReactPlayer
        {...rest}
        config={{
          youtube: {
            playerVars: {
              rel: 0
            }
          }
        }}
        controls={controls ?? true}
        height={'100%'}
        {...image && {
          light: image,
          playIcon: <PlayVideoButton size={units(10)} />
        }}
        url={url}
        volume={volume ?? 0.3}
        width={'100%'}
      />
    </Wrapper>
  );
};

/**
 * Export `VideoPlayer` component.
 */

export default VideoPlayer;
